import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';
export type SortValue = string | number | null | undefined;

export interface SortState {
  key: string;
  dir: SortDir;
  toggle: (key: string) => void;
}

/**
 * Client-side column sorting for a table.
 *
 * Client-side is correct here because every list in PlatformIQ is fully loaded —
 * 45 containers, 75 endpoints. Sorting a page of a server-paginated list would
 * reorder only the rows already fetched, which looks like sorting and is not; if
 * any of these lists ever paginates, this hook must be replaced rather than kept.
 *
 * Two behaviours that are easy to get wrong:
 *
 *  - **Blanks always sort last**, in both directions. A container with no version
 *    is not "before 0.1.0"; it is an absence, and floating absences to the top of
 *    a descending sort buries the rows someone is actually looking for.
 *  - **Numeric-aware string comparison**, so `1.10.0` sorts above `1.9.0`. Plain
 *    lexical order puts 9 above 10 and makes a rollout look like a rollback.
 */
export function useSort<T>(
  rows: T[],
  accessors: Record<string, (row: T) => SortValue>,
  initialKey: string,
  initialDir: SortDir = 'asc',
): { sorted: T[]; state: SortState } {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState<SortDir>(initialDir);

  const sorted = useMemo(() => {
    const accessor = accessors[key];
    if (!accessor) return rows;

    return [...rows].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);

      const aBlank = av === null || av === undefined || av === '';
      const bBlank = bv === null || bv === undefined || bv === '';
      if (aBlank && bBlank) return 0;
      if (aBlank) return 1;
      if (bBlank) return -1;

      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });

      return dir === 'asc' ? cmp : -cmp;
    });
    // accessors is rebuilt each render by callers; keying the memo on it would
    // defeat the memo entirely, and its contents are stable per table.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, key, dir]);

  const toggle = (next: string) => {
    if (next === key) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      // A new column starts ascending. Carrying the previous direction over means
      // the first click on a column sometimes sorts backwards for no visible reason.
      setKey(next);
      setDir('asc');
    }
  };

  return { sorted, state: { key, dir, toggle } };
}
